"use client";

import { useGetAllBlogsQuery } from "@/api/AppApi/services/blogs";
import { useSearchParams } from "next/navigation";
import { useMemo } from "react";
import Paginate from "../../common/pagination/Paginate";
import BlogsPageCardClient from "./blog-page-helpers/BlogsPageCardClient";
import { useIsMobile } from "@/hooks/useIsMobile";
import BlogSortDropdown from "./blog-page-helpers/BlogSortDropdown";
import { useTranslation } from "@/hooks/localization/useTranslations";

const limit = 4;

interface Props {}

const BlogContent: React.FC<Props> = ({}) => {
  const searchParams = useSearchParams();
  const t = useTranslation();
  const isMobile = useIsMobile();

  const queryParams = useMemo(() => {
    const params: Record<string, any> = { limit };
    const category = searchParams?.get("category");
    const date = searchParams?.get("date");
    const authors = searchParams?.get("authors");
    const tags = searchParams?.get("tags");
    const sort_by = searchParams?.get("sort_by");
    const page = searchParams?.get("page");

    if (category) params.category = category;
    if (date) params.date = date;
    if (authors) params.authors = authors;
    if (tags) params.tags = tags;
    if (sort_by) params.sort_by = sort_by;
    if (page) params.page = Number(page);
    return params;
  }, [searchParams]);

  const hasClientFilters = true;

  const { data, isFetching } = useGetAllBlogsQuery({
    params: queryParams,
    enabled: hasClientFilters || !!searchParams?.get("page"),
  });

  const blogs = data?.result?.data ?? [];
  const pageCount = data?.result?.meta?.last_page || 0;

  const showLoading = isFetching && hasClientFilters;
  const isEmpty = !showLoading && (!blogs || blogs.length === 0);

  return (
    <>
      {isMobile && <BlogSortDropdown />}
      {showLoading ? (
        <div className="blog-loading-skeleton">
          {[1, 2, 3].map((i) => (
            <div key={i} className="blog-skeleton-card">
              <div className="skeleton-image skeleton-pulse" />
              <div className="skeleton-content">
                <div className="skeleton-line skeleton-line-short skeleton-pulse" />
                <div className="skeleton-line skeleton-pulse" />
                <div className="skeleton-line skeleton-line-medium skeleton-pulse" />
              </div>
            </div>
          ))}
        </div>
      ) : isEmpty ? (
        <div className="blog-empty-state">
          <div className="empty-state-icon">
            <svg
              style={{ color: "var(--gray3)" }}
              width="120"
              height="120"
              viewBox="0 0 120 120"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="28"
                y="16"
                width="64"
                height="88"
                rx="8"
                className="fill-gray5"
              />
              <rect
                x="36"
                y="8"
                width="48"
                height="16"
                rx="4"
                fill="currentColor"
              />
              <path
                d="M60 50L60 78"
                stroke="currentColor"
                strokeWidth="6"
                strokeLinecap="round"
              />
              <circle cx="60" cy="86" r="3" fill="currentColor" />
            </svg>
          </div>
          <h3 className="empty-state-title">{t.no_posts}</h3>
          <p className="empty-state-description">{t.no_posts_in_category}</p>
        </div>
      ) : (
        <section className="main-content">
          {blogs?.map((blog, key) => (
            <BlogsPageCardClient key={key} blog={blog} />
          ))}
        </section>
      )}
      {!isEmpty && !showLoading && <Paginate pageCount={pageCount} />}
    </>
  );
};

export default BlogContent;
